import { defineComponent, ref, Ref, onMounted, onUnmounted, watch, computed } from 'vue';
import context, { Constants, ITaskCreator, ISmsService, TextConversationModel } from '@truckdown/systems';
import { Constants as LocalConstants } from '../../constants';
import * as signalR from '@microsoft/signalr';

interface IRequestStatus {
    vendor: {
        isActive: boolean;
        new: number;
        active: number;
    },
    fleet: {
        isActive: boolean;
        new: number;
        active: number;
    }
}

interface IProps {
    requestStatus: IRequestStatus;
}

interface IData {
    isAuthenticated: Ref<boolean>;
    isCsr: Ref<boolean>;
    firstName: Ref<string | undefined>;
    lastName: Ref<string | undefined>;
    profilePicture: Ref<string | undefined>;

    showControlPanel: Ref<boolean>,
    adminSearchSelected: Ref<boolean>,
    adminSearchString: Ref<string>

    unassignedTaskCount: Ref<number>;
    taskCount: Ref<number>;

    unreadConversations: Ref<number>;
    conversationCount: Ref<number>;

    requestCounts: Ref<IRequestStatus>;
    showRequestsMenu: Ref<boolean>;

    showFleetSetupWidget: Ref<boolean>;
    showMenu: Ref<boolean>;
}

const getData = function (props: IProps) {
    return {
        isAuthenticated: ref<boolean>(false),
        isCsr: ref<boolean>(false),
        firstName: ref<string | undefined>(),
        lastName: ref<string | undefined>(),
        profilePicture: ref<string | undefined>(),

        showControlPanel: ref<boolean>(false),
        adminSearchSelected: ref<boolean>(false),
        adminSearchString: ref<string>(''),

        unassignedTaskCount: ref<number>(0),
        taskCount: ref<number>(0),

        unreadConversations: ref<number>(0),
        conversationCount: ref<number>(0),

        requestCounts: ref<IRequestStatus>(props.requestStatus),
        showRequestsMenu: ref<boolean>(false),

        showFleetSetupWidget: ref<boolean>(false),
        showMenu: ref<boolean>(false)
    } as IData;
}

const setData = function (data: IData) {
    data.isAuthenticated.value = context.isAuthenticated;
    data.isCsr.value = context.isInRole(Constants.Roles.SystemRole)
        || context.isInRole(Constants.Roles.CSRRole);

    data.firstName.value = context.user?.firstName;
    data.lastName.value = context.user?.lastName;

    const profileSetting = context.settings['profilepic'];
    const apiUrl = context.apis['api'].url;
    
    if (profileSetting) {
        data.profilePicture.value = apiUrl + '/systems/profile-image/' + encodeURIComponent(profileSetting);
    } else {
        data.profilePicture.value = apiUrl + '/systems/profile-image';
    }

    data.showFleetSetupWidget.value = fleetSetupWidgetCheck();
}

const fleetSetupWidgetCheck = function (): boolean {
    if (!context.isAuthenticated) {
        return false;
    }

    if (context.isInRole("General Admin")
        && !context.isInRole("Fleet User")
        && context.settings["accounttype"] != "Vendor"
        && context.settings["fleet_onboarding"] != "complete"
        && context.settings["fleet_onboarding"] != "dismissed") {
        return true;
    }

    return false;
}

const getComputed = function (data: IData) {
    const getNewRequestCount = function (counts: IRequestStatus) {
        return (counts.vendor.isActive ? counts.vendor.new : 0)
            + (counts.fleet.isActive ? counts.fleet.new : 0);
    }

    const getActiveRequestCount = function (counts: IRequestStatus) {
        return (counts.vendor.isActive ? counts.vendor.active : 0)
            + (counts.fleet.isActive ? counts.fleet.active : 0);
    }

    return {
        useRequestMenu: computed(() => {
            if (data.requestCounts.value.fleet.isActive && data.requestCounts.value.vendor.isActive) {
                return true;
            }
            return false;
        }),
        requestsUrl: computed(() => {
            if (data.requestCounts.value.vendor.isActive) {
                return '/servicerequests/vendor';
            }
            return '/servicerequests/fleet';
        }),
        requestBadgeCount: computed(() => {
            return getNewRequestCount(data.requestCounts.value);
        }),
        requestTooltip: computed(() => {
            return 'Service Requests\n   New: ' + getNewRequestCount(data.requestCounts.value) + '\n   Active: ' + getActiveRequestCount(data.requestCounts.value);
        }),
        requestVendorTooltip: computed(() => {
            return 'Received Service Requests\n   New: ' + data.requestCounts.value.vendor.new + '\n   Active: ' + data.requestCounts.value.vendor.active;
        }),
        requestVendorCount: computed(() => {
            return data.requestCounts.value.vendor.new + data.requestCounts.value.vendor.active;
        }),
        requestFleetTooltip: computed(() => {
            return 'Sent Service Requests\n   New: ' + data.requestCounts.value.fleet.new + '\n   Active: ' + data.requestCounts.value.fleet.active;
        }),
        requestFleetCount: computed(() => {
            return data.requestCounts.value.vendor.new + data.requestCounts.value.vendor.active;
        }),
        conversationTitle: computed(() => {
            const conversations = data.conversationCount.value + ' conversation' + (data.conversationCount.value == 1 ? '' : 's');
            const unread = data.unreadConversations.value + ' unread';
            return conversations + ', ' + unread;
        })
    }
}

const getMethods = function (data: IData) {
    return {
        toggleMenu: function (evt: Event) {
            data.showMenu.value = !data.showMenu.value;
            if (data.showMenu.value) {
                data.showRequestsMenu.value = false;
            }
        },
        closeMenu: function (evt: Event) {
            data.showMenu.value = false;
        },
        closeRequestMenu: function (evt: Event) {
            data.showRequestsMenu.value = false;
        },
        toggleRequestMenu: function (evt: Event) {
            data.showRequestsMenu.value = !data.showRequestsMenu.value;
            if (data.showRequestsMenu.value) {
                data.showMenu.value = false;
            }
        },
        toggleControlPanel: function (evt: Event) {
            data.showControlPanel.value = !data.showControlPanel.value;
        },

        closeControlPanel: function (evt: Event) {
            data.showControlPanel.value = false;
        },

        adminSearchFocusInput: function (evt: Event) {
            data.adminSearchSelected.value = true;
        },

        adminSearchBlurInput: function (evt: Event) {
            data.adminSearchSelected.value = false;
        },

        adminSearchInputKeyUp: function (evt: KeyboardEvent) {
            if (!data.showControlPanel.value) {
                data.showControlPanel.value = true;
                if (evt.target && (evt.target as any).blur) {
                    (evt.target as any).blur();
                }
            }
        }
    };
}

const assignTaskCounts = async function (data: IData) {
    try {
        if (data.isCsr.value) {
            const service = await context.getService<ITaskCreator>('ITaskCreator');
            const res = await service.getCounts();
            if (res.ok) {
                data.taskCount.value = res.data ?? 0;
                data.unassignedTaskCount.value = res.data2 ?? 0;
            } else {
                data.unassignedTaskCount.value = 0;
                data.taskCount.value = 0;
            }
        } else {
            data.unassignedTaskCount.value = 0;
            data.taskCount.value = 0;
        }

    }
    catch (err) {
        console.log(err);
        data.unassignedTaskCount.value = 0;
        data.taskCount.value = 0;
    }
}

const setConversationCounts = async function (data: IData) {
    try {
        if (data.isCsr.value) {
            let service = await context.getService<ISmsService>('ISmsService');
            let res = await service.conversationCount();
            if (res.ok) {
                data.conversationCount.value = res.data ?? 0;
                data.unreadConversations.value = res.data2 ?? 0;
            } else {
                console.log('Error getting conversation counts');
            }
        }
    }
    catch (err) {
        console.log(err);
    }
}

const initializeWebsocket = function (data: IData) {
    if (data.isCsr.value) {
        let connection = new signalR.HubConnectionBuilder()
            .withUrl('/systems/csr-hub')
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
                    if (retryContext.elapsedMilliseconds < 60000) { // Retry for 1 minute
                        return 5000; //try every 5 seconds for 1 min
                    } else if (retryContext.elapsedMilliseconds < 900000) {
                        return 10000; //try every 10 seconds for 15 minutes
                    } else {
                        return 60000; //try every minute
                    }
                }
            })
            .build();

        connection.onreconnecting((error: any) => {
            console.warn("Connection lost. Reconnecting...", error);
        });

        connection.onreconnected(async () => {
            console.log("Connection reestablished.");
            await setConversationCounts(data);
        });

        connection.onclose((error: any) => {
            console.error("Connection closed.", error);
        });

        connection.on('updateSupportConversation', async (val: TextConversationModel) => {
            try {
                context.events.emit('updateSupportConversation', val);
            }
            catch (err) {
                console.log('Error receiving updateSupportConversation message', err, val);
            }
        });

        connection.on('new-task', async (val: any) => {
            try {
                context.events.emit('new-task', val);
            }
            catch (err) {
                console.log('Error receiving newTask message', err, val);
            }
        });

        connection.on('update-task', async (val: any) => {
            try {
                context.events.emit('update-task', val);
            }
            catch (err) {
                console.log('Error receiving updateTask message', err, val);
            }
        });

        connection.on('update-task-counts', async (val: any) => {
            try {
                context.events.emit('update-task-counts', val);
            }
            catch (err) {
                console.log('Error receiving updateTaskCounts message', err, val);
            }
        });

        connection.start().catch((err: any) => console.log('Websocket connection error', err));
        return connection;
    }
}

export default defineComponent({
    name: 'header-icons',
    props: {
        requestStatus: {
            type: Object as () => IRequestStatus,
            required: true
        }
    },
    setup: function (props: IProps) {
        const data = getData(props);
        setData(data);

        const computed = getComputed(data);
        const methods = getMethods(data);

        watch(() => props.requestStatus, function (value, oldValue) {
            data.requestCounts.value = value;
        });

        const refreshContext = function () {
            setData(data);
        }
        const handleUpdateTask = async function () {
            await assignTaskCounts(data);
        }
        const handleUpdateConversations = async function () {
            await setConversationCounts(data);
        }

        onMounted(async () => {
            context.events.on(LocalConstants.Events.RefreshOnboardingProgress, refreshContext);

            if (data.isCsr.value) {
                await assignTaskCounts(data);
                await setConversationCounts(data);
                initializeWebsocket(data);

                context.events.on('new-task', handleUpdateTask);
                context.events.on('update-task', handleUpdateTask);
                context.events.on('update-task-counts', handleUpdateTask);
                context.events.on('updateSupportConversation', handleUpdateConversations);
            }
        });

        onUnmounted(() => {
            context.events.off(LocalConstants.Events.RefreshOnboardingProgress, refreshContext);
            if (data.isCsr.value) {
                context.events.off('new-task', handleUpdateTask);
                context.events.off('update-task', handleUpdateTask);
                context.events.off('update-task-counts', handleUpdateTask);
                context.events.off('updateSupportConversation', handleUpdateConversations);
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});